import { FormEvent, RefObject, useEffect, useRef } from 'react'

interface Props {
  /** Lets the page focus the field — e.g. after its own "Show all articles"
   *  exit, which unmounts the button that was clicked and would otherwise drop
   *  focus to <body>. Optional; the field manages its own ref when absent. */
  inputRef?: RefObject<HTMLInputElement | null>
  /** The text in the field. Controlled by the page so `?q=` can seed it. */
  value: string
  onChange: (value: string) => void
  /** Fires on Enter / form submit only. Search is submit-driven, not as-you-type. */
  onSubmit: () => void
  /** Clears the field AND exits search mode. */
  onClear: () => void
  /** True while search results are on screen. Drives the clear affordance —
   *  see the note on `value || active` below. */
  active?: boolean
  /** Disables nothing — see the note below. Used only to show progress. */
  pending?: boolean
}

/**
 * Search field for the Saved list — SEARCH_PLAN.md §7.
 *
 * Submit-driven on purpose. As-you-type was considered and dropped: for a
 * read-later library the query is "I know I saved something about X", which is a
 * once-per-session, submit-shaped interaction. It also deletes work that would
 * otherwise be mandatory — a debounce, a query ceiling, and an IME composition
 * guard (with submit semantics, multi-stage input methods have already committed
 * before the query fires).
 *
 * The field is never disabled while a request is in flight. A disabled input
 * invites the user to work out why, and it would block them from editing a query
 * they have already decided is wrong.
 */
export default function SearchBar({
  inputRef: externalRef,
  value,
  onChange,
  onSubmit,
  onClear,
  active,
  pending,
}: Props) {
  const ownRef = useRef<HTMLInputElement>(null)
  const inputRef = externalRef ?? ownRef

  // `/` focuses the field, the way it does in most searchable apps — but only
  // when the user is not already typing somewhere, otherwise it eats the slash
  // in a URL being pasted into the save box.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      const el = document.activeElement
      const typing =
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        (el instanceof HTMLElement && el.isContentEditable)
      if (typing) return
      e.preventDefault()
      inputRef.current?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
    // `inputRef` is in the deps because it is now an expression
    // (`externalRef ?? ownRef`) rather than a stable useRef result. Both branches
    // are ref objects with stable identity, so this does not re-subscribe.
  }, [inputRef])

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    onSubmit()
  }

  return (
    <form className="search-bar" role="search" onSubmit={handleSubmit}>
      <label className="sr-only" htmlFor="article-search">
        Search your saved articles
      </label>
      {/* An inline SVG, not 🔍. The emoji was the only colour emoji used as
          chrome anywhere in the app: it renders in the vendor's palette rather
          than the brand's, ignores `color`, and changes shape per platform. */}
      <svg
        className="search-bar-icon"
        aria-hidden="true"
        viewBox="0 0 16 16"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      >
        <circle cx="7" cy="7" r="4.5" />
        <path d="M10.5 10.5 L14 14" />
      </svg>
      <input
        id="article-search"
        ref={inputRef}
        type="search"
        className="search-bar-input"
        placeholder="Search your saved articles"
        value={value}
        autoComplete="off"
        // The browser's own clear affordance would clear the text without
        // exiting search mode, leaving results on screen with an empty box.
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            onClear()
            // Deliberately NO blur. Escape in a search field is clear-and-stay:
            // blurring throws a keyboard user back to the top of the document
            // for their next Tab, and it made the two exits disagree — the ✕
            // below refocuses the input. Same key, same field, same result now.
          }
        }}
      />
      {pending && <span className="search-bar-spinner" aria-hidden="true" />}
      {/* `value || active`, NOT `value` alone. Binding this to the text was a
          real bug: delete the query by hand rather than clicking here, and the
          results stayed on screen while the only visible way out disappeared.
          The affordance belongs to "results are showing", not to "the box has
          text in it". */}
      {(value || active) && (
        <button
          type="button"
          className="search-bar-clear"
          onClick={() => {
            onClear()
            inputRef.current?.focus()
          }}
          aria-label="Clear search"
        >
          {/* × (U+00D7), not ✕ (U+2715). The three other dismiss affordances in
              the app use ×, and the tag chip directly above this one is one of
              them. The hit target is widened to 44px by a pseudo-element in
              App.css rather than by padding, so the field stays compact. */}
          ×
        </button>
      )}
    </form>
  )
}
